import type {
  BlockDefinition,
  BlockVersionMeta,
  BorderEdge,
  LeafPrimitive,
  Placement,
  RegionBorders,
  VariableMeta,
  Zone,
  ZoneStyle,
} from '@tsagko/label-contracts'
import { getPrinterProfile, getPrinterProfiles } from '@tsagko/label-printer-profiles'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useAdapter } from '../adapters/AdapterProvider'
import { useUnsavedGuard } from '../state/useUnsavedGuard'
import { useConfirm } from '../ui/ConfirmDialog'
import { useToast } from '../ui/Toaster'
import { Canvas } from '../canvas/Canvas'
import { useCanvasDeselect } from '../canvas/useCanvasDeselect'
import { useResolvedImages } from '../canvas/useResolvedImages'
import { CanvasControls } from '../canvas/CanvasControls'
import { computeZoneLayout, type LaidOutZone } from '../canvas/layout'
import { Ruler } from '../canvas/Ruler'
import { clampZoom, fitZoomToViewport } from '../canvas/transforms'
import {
  findLeafComponent,
  findZoneByPath,
  replaceComponentAtPath,
  resolveBoxAtPath,
  setSplitRatioAtPath,
  setZoneBordersAtPath,
  setZoneStyleAtPath,
  zoneIdAtPath,
} from '../canvas/tree-ops'
import { findCoincidentEdge } from '../canvas/border-ops'
import { hasContent, mergeZone, normalizeBlockLayout, splitZone, type SplitDirection } from '../canvas/zone-ops'
import { useHistory } from '../state/useHistory'
import { PreviewPane } from '../preview/PreviewPane'
import { getStoredPreviewProfile } from '../preview/profile-storage'
import { SplitPane } from '../preview/SplitPane'
import { ForkBlockModal } from './ForkBlockModal'
import { VersionHistoryPanel } from './VersionHistoryPanel'
import { BlockMetadataEditor } from './BlockMetadataEditor'
import { RegionPropertyPanel } from './RegionPropertyPanel'
import { RegionTable } from './RegionTable'
import { RulerDimensionInput } from '../canvas/RulerDimensionInput'
import { DimensionLockButton } from '../canvas/DimensionLockButton'
import { clampBlockDimensionMm, ratioForChildSize, snapToMm } from './dimension-ops'
import { deriveBadgeState } from './badge-state'
import { mergeVariables, pruneOrphans } from './variables-ops'
import { StatusBadge } from './StatusBadge'
import { blockPreviewTemplate } from './block-preview'

const PX_PER_MM_BASE = 6

// Loaded blocks may carry template-shaped bare leaves saved while mergeZone
// lacked the block filler (Gitea issue #6) — heal them before they become the
// history/dirty baseline. Identity-stable when the layout is already clean.
function healBareLeaves(resource: BlockDefinition): BlockDefinition {
  const layout = normalizeBlockLayout(resource.layout)
  return layout === resource.layout ? resource : { ...resource, layout }
}

/**
 * Seed document for a brand-new block.
 *
 * `name` and the root region's `content` are SEEDED DATA, not view chrome:
 * they are persisted on the BlockDefinition and the content string is baked
 * into the rendered ZPL. They are therefore resolved ONCE, from the caller's
 * `t()`, in whatever language the author was working in when they hit "new"
 * — and never re-translated afterwards (issue #154). Keeping the module pure
 * (labels in, no `t()` here) matches `validateBlockId` in ./block-id.
 */
function makeBlankBlock(id: string, labels: { name: string; content: string }): BlockDefinition {
  return {
    id,
    name: labels.name,
    version: 1,
    tier: 'custom',
    widthMm: 80,
    heightMm: 40,
    variables: {},
    layout: {
      id: 'root',
      component: {
        type: 'text',
        id: 'c-root',
        value: { content: labels.content },
      },
    },
  }
}

function ViewKebabMenu({ onFork }: { onFork: () => void }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  return (
    <div className="relative">
      <button
        type="button"
        data-testid="view-kebab"
        aria-label={t('blockConfigurator.toolbar.moreActions')}
        onClick={() => setOpen(o => !o)}
        className="rounded border border-stone-300 px-2 py-1 text-xs hover:bg-stone-100 dark:border-stone-700 dark:hover:bg-stone-800"
      >
        ⋮
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1 w-48 rounded border border-stone-200 bg-white shadow-lg dark:border-stone-700 dark:bg-stone-900">
          <button
            type="button"
            onClick={() => {
              setOpen(false)
              onFork()
            }}
            className="block w-full px-3 py-1.5 text-left text-xs hover:bg-stone-100 dark:hover:bg-stone-800"
          >
            {t('blockConfigurator.toolbar.fork')}
          </button>
        </div>
      )}
    </div>
  )
}

export function BlockConfiguratorRoute() {
  const adapter = useAdapter()
  const navigate = useNavigate()
  const { blockId } = useParams<{ blockId: string }>()
  const isNewRoute = !blockId || blockId === 'new'
  // Declared up here (not next to the other view hooks below) because the
  // blank-block seed needs it before `useHistory` takes its initial value.
  const { t } = useTranslation()

  const initialBlock = useMemo(
    () =>
      makeBlankBlock(blockId && blockId !== 'new' ? blockId : `draft-${Date.now()}`, {
        name: t('blockConfigurator.blank.name'),
        content: t('blockConfigurator.blank.content'),
      }),
    // Recomputing on a locale switch is a no-op by design: `useHistory` and
    // `lastSavedRef` both read `initialBlock` only on mount, so the seeded
    // strings stay in the language the block was created in.
    [blockId, t]
  )

  const history = useHistory<BlockDefinition>(initialBlock)
  // Transient document during a bounds-resize drag. Written on every
  // pointermove; committed once to history on pointerup. NOT visible to
  // the dirty-check (lastSavedRef compares history.state, not transientBlock).
  const [transientBlock, setTransientBlock] = useState<BlockDefinition | undefined>(undefined)
  // All reads during render use the effective block so the canvas stays live.
  const block = transientBlock ?? history.state

  const [selectedPath, setSelectedPath] = useState<string[] | undefined>(undefined)
  const [zoom, setZoom] = useState(1)
  const [snapToGrid, setSnapToGrid] = useState(false)
  const [etag, setEtag] = useState<string | undefined>()
  const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'saved' | 'error'>('idle')
  const [saveError, setSaveError] = useState<string | undefined>()
  const [conflictPending, setConflictPending] = useState(false)
  // New-block route opens in draft state (authorable from the start).
  // Existing-block route resolves to draft-or-view in the load effect below.
  const [isDraft, setIsDraft] = useState<boolean>(() => isNewRoute)
  // Load-state machine: 'loading' while in-flight, 'ok', 'not-found', 'error'.
  const [loadState, setLoadState] = useState<'loading' | 'ok' | 'not-found' | 'error'>(
    isNewRoute ? 'ok' : 'loading'
  )
  const [loadError, setLoadError] = useState<string | undefined>()
  // Monotonic token: incremented on every load start (effect or Retry) and on
  // effect cleanup, so any in-flight load that no longer matches is stale and
  // must not set state (unmount or blockId change without a remount).
  const loadSeqRef = useRef(0)

  // Dirty tracking: compare present document identity against last-saved snapshot.
  // lastSavedRef is set on: initial blank, after a successful save, and after load.
  // Dirty is only meaningful in draft mode (view mode is read-only).
  // NOTE: always compare history.state (not the transient doc) so a mid-drag
  // state doesn't falsely mark the doc dirty.
  const lastSavedRef = useRef<BlockDefinition>(initialBlock)
  const dirty = isDraft && history.state !== lastSavedRef.current
  const [forkOpen, setForkOpen] = useState(false)
  // When forking a specific historical version, forkSource carries the loaded
  // body and the version number to show in the modal subtitle. When undefined
  // the modal forks the current in-memory block (kebab "Fork from this version").
  const [forkSource, setForkSource] = useState<{ body: BlockDefinition; version: number } | undefined>(undefined)
  const [existingBlockIds, setExistingBlockIds] = useState<string[]>([])
  const [blockListLoading, setBlockListLoading] = useState(true)
  const [blockListFailed, setBlockListFailed] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [versions, setVersions] = useState<BlockVersionMeta[]>([])
  const [latestPublishedVersion, setLatestPublishedVersion] = useState<number | undefined>(undefined)
  // True once listBlockVersions has returned (ok, including empty list).
  // Distinguishes "loaded, 0 versions (draft-only)" from "not fetched yet".
  const [versionsLoaded, setVersionsLoaded] = useState(false)
  // Track id validity for new blocks so Save can be gated.
  // Start with true (the generated draft-{timestamp} id is always valid on load).
  const [idValid, setIdValid] = useState(true)

  // Next version to be published: latestPublishedVersion + 1, or 1 for a brand-new block.
  const nextVersion = (latestPublishedVersion ?? 0) + 1

  const confirm = useConfirm()
  const toast = useToast()

  const canvasContainerRef = useRef<HTMLDivElement>(null)
  const canvasPaneRef = useRef<HTMLDivElement>(null)
  const [dragging, setDragging] = useState<'right' | 'bottom' | 'corner' | undefined>()

  const effectivePxPerMm = PX_PER_MM_BASE * zoom

  // Full load sequence (draft-probe → latest-probe → version list), shared by
  // the mount/param-change effect and the Retry button. `isCancelled` is
  // checked after every await so a stale run (superseded, unmounted) never
  // sets state.
  const runLoad = useCallback(
    async (isCancelled: () => boolean) => {
      if (!adapter || !blockId || isNewRoute) return
      setLoadState('loading')
      setLoadError(undefined)
      // Probe for a draft first. If present, prefer it.
      const draftRes = await adapter.resources.loadBlock(blockId, 'draft')
      if (isCancelled()) return

      // Resolve the body (draft or latest) WITHOUT committing any state yet —
      // committing here would create an intermediate render where block.version
      // already reflects the new load but latestPublishedVersion still reflects
      // whatever it was before this load started (or vice versa). Both fetches
      // are awaited first; every piece of state is then set together, below,
      // in a single synchronous pass.
      let body: { resource: BlockDefinition; etag: string; isDraft: boolean } | undefined
      if (draftRes.status === 'ok') {
        body = { resource: draftRes.data.resource, etag: draftRes.data.etag, isDraft: true }
      } else if (draftRes.error.code !== 'NOT_FOUND') {
        // Only NOT_FOUND means "no draft". Any other draft-probe failure is a
        // real error — surface it instead of silently falling back to latest
        // (which could mask a draft the user expects to keep editing).
        setLoadError(draftRes.error.message)
        setLoadState('error')
        return
      } else {
        // No draft — load the latest published.
        const latestRes = await adapter.resources.loadBlock(blockId)
        if (isCancelled()) return
        if (latestRes.status === 'ok') {
          body = { resource: latestRes.data.resource, etag: latestRes.data.etag, isDraft: false }
        } else if (latestRes.error.code === 'NOT_FOUND') {
          // Both draft-probe and latest-probe returned NOT_FOUND → block doesn't exist.
          setLoadState('not-found')
          return
        } else {
          setLoadError(latestRes.error.message)
          setLoadState('error')
          return
        }
      }

      // Always fetch the version list so the badge can compute the next version.
      const versionsRes = await adapter.resources.listBlockVersions(blockId)
      if (isCancelled()) return

      // Commit the body and the version list together — same tick, no fetch
      // in between — so the badge never derives from a mix of fresh body +
      // stale version list (or vice versa).
      const healed = healBareLeaves(body.resource)
      history.reset(healed)
      setEtag(body.etag)
      setIsDraft(body.isDraft)
      // Loaded body is the clean baseline — not dirty (view mode is
      // read-only regardless).
      lastSavedRef.current = healed
      setLoadState('ok')
      if (versionsRes.status === 'ok') {
        setVersions(versionsRes.data)
        const max = versionsRes.data.reduce(
          (acc, v) => (v.version > acc ? v.version : acc),
          0
        )
        setLatestPublishedVersion(max > 0 ? max : undefined)
        // Mark versions as loaded so the badge can distinguish "loaded empty"
        // (new block, first save) from "not yet fetched" (shows '?').
        setVersionsLoaded(true)
      }
    },
    [adapter, blockId, isNewRoute, history],
  )

  // Start a load run under a fresh token. Any previously in-flight run
  // (effect- or Retry-started) is invalidated by the increment.
  const startLoad = useCallback(() => {
    const mySeq = ++loadSeqRef.current
    void runLoad(() => loadSeqRef.current !== mySeq)
  }, [runLoad])

  useEffect(() => {
    if (isNewRoute) {
      // Param can change without a remount — landing on /blocks/new from a
      // failed id must reset the machine to the editable blank state.
      setLoadState('ok')
      setLoadError(undefined)
      return
    }
    if (!adapter) return
    startLoad()
    return () => {
      // Invalidate any in-flight load (effect- or Retry-started) on unmount
      // or adapter/blockId change.
      loadSeqRef.current++
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adapter, blockId])

  // Lazy block-id list: needed for new-block id validation and the fork
  // modal's uniqueness check. Fetched on first need (new-block route or fork
  // modal opening) — a plain existing-block open never issues the fetch.
  // The started flag resets when a fetch is cancelled before settling
  // (deps change/unmount) AND when a fetch settles with failure, so the next
  // trigger (e.g. fork modal re-open) re-issues it — keeps the modal's
  // "Try again in a moment" copy true.
  const blockListFetchStartedRef = useRef(false)
  useEffect(() => {
    if (!adapter) return
    if (!(isNewRoute || forkOpen)) return
    if (blockListFetchStartedRef.current) return
    blockListFetchStartedRef.current = true
    let cancelled = false
    let settled = false
    setBlockListLoading(true)
    setBlockListFailed(false)
    void (async () => {
      const res = await adapter.resources.listBlocks({ limit: 1000 })
      if (cancelled) return
      settled = true
      if (res.status === 'ok') {
        setExistingBlockIds(res.data.items.map(x => x.id))
      } else {
        setBlockListFailed(true)
        blockListFetchStartedRef.current = false
      }
      setBlockListLoading(false)
    })()
    return () => {
      cancelled = true
      if (!settled) blockListFetchStartedRef.current = false
    }
  }, [adapter, isNewRoute, forkOpen])

  useEffect(() => {
    if (!historyOpen || !adapter || !blockId || isNewRoute) return
    let cancelled = false
    void (async () => {
      const res = await adapter.resources.listBlockVersions(blockId)
      if (cancelled) return
      if (res.status === 'ok') setVersions(res.data)
    })()
    return () => {
      cancelled = true
    }
  }, [historyOpen, adapter, blockId, isNewRoute])

  const selectedPrimitive = useMemo<LeafPrimitive | undefined>(() => {
    if (!selectedPath) return undefined
    return findLeafComponent(block.layout, selectedPath)
  }, [block, selectedPath])

  const selectedZoneId = useMemo<string | undefined>(() => {
    if (!selectedPath) return undefined
    return zoneIdAtPath(block.layout, selectedPath)
  }, [block, selectedPath])

  const selectedZoneStyle = useMemo<ZoneStyle | undefined>(() => {
    if (!selectedPath) return undefined
    // Walk the layout following the path to extract the zone's style.
    let node: Zone = block.layout
    for (const seg of selectedPath) {
      const idx = seg === '0' ? 0 : seg === '1' ? 1 : undefined
      if (idx === undefined || !node.children) return undefined
      node = node.children[idx]
    }
    return node.style
  }, [block.layout, selectedPath])

  const selectedBorders = useMemo<RegionBorders | undefined>(() => {
    if (!selectedPath) return undefined
    let node: Zone = block.layout
    for (const seg of selectedPath) {
      const idx = seg === '0' ? 0 : seg === '1' ? 1 : undefined
      if (idx === undefined || !node.children) return undefined
      node = node.children[idx]
    }
    return node.borders
  }, [block.layout, selectedPath])

  // Selected zone's laid-out geometry (mm), for the region-size (mm) editor
  // (issue #126) — mirrors TemplateDesignerRoute's selectedZoneForPanel.
  const selectedZoneForPanel = useMemo<LaidOutZone | undefined>(() => {
    if (!selectedPath) return undefined
    const layout = computeZoneLayout(block.layout, { widthMm: block.widthMm, heightMm: block.heightMm })
    return layout.find(
      z => z.path.length === selectedPath.length && z.path.every((p, i) => p === selectedPath[i])
    )
  }, [block, selectedPath])

  const variablesForPanel = useMemo<Record<string, VariableMeta>>(
    () => mergeVariables(block.layout, block.variables ?? {}),
    [block.layout, block.variables]
  )

  // New-block Save gate: the id must be pattern-valid AND verifiably unique.
  // While the block list is loading (or its fetch failed) uniqueness can't be
  // checked, so Save stays blocked — same posture as the fork modal's confirm.
  const newBlockSaveBlocker = !isNewRoute
    ? undefined
    : !idValid
      ? t('blockConfigurator.saveBlocker.invalidId')
      : blockListLoading
        ? t('blockConfigurator.saveBlocker.checkingIds')
        : blockListFailed
          ? t('blockConfigurator.saveBlocker.idCheckFailed')
          : undefined

  const badgeState = useMemo(
    () =>
      deriveBadgeState({
        blockId,
        isDraft,
        currentVersion: block.version,
        latestPublishedVersion,
        versionsLoaded,
      }),
    [blockId, isDraft, block.version, latestPublishedVersion, versionsLoaded]
  )

  const onSelect = useCallback(
    (zone: LaidOutZone) => {
      setSelectedPath(zone.path)
    },
    []
  )

  const onSplit = useCallback(
    (zone: LaidOutZone, direction: SplitDirection) => {
      history.update(prev => ({
        ...prev,
        layout: splitZone(prev.layout, zone.path, direction),
      }))
      setSelectedPath(undefined)
    },
    [history]
  )

  const onSplitRatioChange = useCallback(
    (path: string[], ratio: number) => {
      // Write to transient state during the drag so we don't flood history.
      // Re-base off `transientBlock ?? history.state` (unlike
      // updateDimensionsFromPointer, which re-bases off `history.state`):
      // ratio updates must compose across the in-progress transient so a drag
      // touching multiple dividers keeps earlier edits, whereas dimension
      // writes are absolute pointer-derived values that fully replace the
      // dragged fields each move.
      const base = transientBlock ?? history.state
      setTransientBlock({ ...base, layout: setSplitRatioAtPath(base.layout, path, ratio) })
    },
    [history.state, transientBlock]
  )

  const onSplitRatioDragEnd = useCallback(() => {
    if (transientBlock !== undefined) {
      history.set(transientBlock)
      setTransientBlock(undefined)
    }
  }, [transientBlock, history])

  const onMergeAtPath = useCallback(
    async (path: string[]) => {
      // Walk the current layout following the canvas's index-based path to
      // find the split node at `path`.
      let node: Zone = block.layout
      for (const seg of path) {
        const idx = Number(seg)
        if (!node.children || Number.isNaN(idx) || (idx !== 0 && idx !== 1)) return
        node = node.children[idx]
      }
      if (!node.children || node.children.length !== 2 || !node.split) return
      const left = node.children[0]
      const right = node.children[1]
      const leftHas = hasContent(left)
      const rightHas = hasContent(right)
      const bothHaveContent = leftHas && rightHas

      // Instant merge ONLY when both sides are empty. Any content on EITHER
      // side routes through the confirm dialog — even the "no data loss"
      // one-side case, since deleting the divider still restructures the
      // layout (the surviving content moves into the merged region).
      if (leftHas || rightHas) {
        const ok = await confirm({
          title: t('confirm.deleteDivider.title'),
          body: bothHaveContent
            ? t('confirm.deleteDivider.bodyBothContent')
            : t('confirm.deleteDivider.bodyMerge'),
          confirmLabel: t('common.delete'),
          danger: bothHaveContent,
        })
        if (!ok) return
        setSelectedPath(undefined)
        const keep: 0 | 1 | 'none' = bothHaveContent ? 'none' : leftHas ? 0 : 1
        history.update(prev => ({ ...prev, layout: mergeZone(prev.layout, path, keep) }))
        return
      }

      // Both empty — silent instant merge (clear selection: a descendant of
      // the merged split may have been selected and no longer exists).
      setSelectedPath(undefined)
      history.update(prev => ({ ...prev, layout: mergeZone(prev.layout, path, 0) }))
    },
    [block.layout, history, confirm, t]
  )

  const onChangeComponent = useCallback(
    (next: LeafPrimitive) => {
      if (!selectedPath) return
      history.update(prev => ({
        ...prev,
        layout: replaceComponentAtPath(prev.layout, selectedPath, next),
      }))
    },
    [history, selectedPath]
  )

  const onChangeZoneStyle = useCallback(
    (next: ZoneStyle | undefined) => {
      if (!selectedPath) return
      history.update(prev => ({
        ...prev,
        layout: setZoneStyleAtPath(prev.layout, selectedPath, next),
      }))
    },
    [history, selectedPath]
  )

  const onSetEdge = useCallback(
    (edge: keyof RegionBorders, value: BorderEdge | undefined) => {
      if (!selectedPath) return
      history.update(prev => {
        const dims = { widthMm: prev.widthMm, heightMm: prev.heightMm }
        const partner = findCoincidentEdge(prev.layout, dims, selectedPath, edge)
        let next = setZoneBordersAtPath(prev.layout, selectedPath, edge, value)
        if (partner) next = setZoneBordersAtPath(next, partner.path, partner.edge, value)
        return { ...prev, layout: next }
      })
    },
    [history, selectedPath]
  )

  const onChangeVariableMeta = useCallback(
    (name: string, meta: VariableMeta) => {
      history.update(prev => ({
        ...prev,
        variables: { ...(prev.variables ?? {}), [name]: meta },
      }))
    },
    [history]
  )

  const onChangePlacement = useCallback(
    (next: Placement | undefined) => {
      if (!selectedPrimitive) return
      // `placement` is optional on every LeafPrimitive variant; cast through
      // a partial so we don't have to enumerate variants here.
      const nextPrim = { ...selectedPrimitive, placement: next } as LeafPrimitive
      onChangeComponent(nextPrim)
    },
    [onChangeComponent, selectedPrimitive]
  )

  const onChangeMetadata = useCallback(
    (next: BlockDefinition) => {
      history.update(() => next)
    },
    [history]
  )

  // Dimension lock (persists with the document): blocks the typed-input
  // commit path and the resize drag handles, not just the disabled styling.
  const dimensionsLocked = block.dimensionsLocked === true

  const onToggleDimensionsLock = useCallback(() => {
    history.update(prev => ({ ...prev, dimensionsLocked: !(prev.dimensionsLocked === true) }))
  }, [history])

  const onChangeWidth = useCallback((widthMm: number) => {
    if (dimensionsLocked) return
    history.update(prev => ({ ...prev, widthMm }))
  }, [history, dimensionsLocked])

  const onChangeHeight = useCallback((heightMm: number) => {
    if (dimensionsLocked) return
    history.update(prev => ({ ...prev, heightMm }))
  }, [history, dimensionsLocked])

  // Region-size (mm) editor commit (issue #126). Root zone (no parent split)
  // resizes the block itself, mirroring the ruler inputs. A child zone's
  // desired mm converts to the parent split's ratio via ratioForChildSize —
  // no data-model change, the ratio-based Zone/Split schema stays as-is. A
  // block's own layout never contains baked instances, so (unlike
  // TemplateDesignerRoute's onZoneDimensionsChange) there is no block-floor
  // clamp to apply here.
  const onZoneDimensionsChange = useCallback(
    ({ widthMm, heightMm }: { widthMm: number; heightMm: number }) => {
      if (!selectedPath) return
      if (selectedPath.length === 0) {
        if (dimensionsLocked) return
        history.update(prev => ({
          ...prev,
          widthMm: clampBlockDimensionMm(widthMm),
          heightMm: clampBlockDimensionMm(heightMm),
        }))
        return
      }
      history.update(prev => {
        const parentPath = selectedPath.slice(0, -1)
        const childIndex = selectedPath[selectedPath.length - 1]
        const parent = findZoneByPath(prev.layout, parentPath)
        if (!parent || !parent.split || !parent.children) return prev
        const axis = parent.split.direction
        const parentBox = resolveBoxAtPath(prev.layout, parentPath, {
          x: 0,
          y: 0,
          w: prev.widthMm,
          h: prev.heightMm,
        })
        if (!parentBox) return prev
        const parentTotal = axis === 'row' ? parentBox.w : parentBox.h
        const desiredChildMm = axis === 'row' ? widthMm : heightMm
        // For child[1]: convert its desired size to the equivalent child[0] size.
        const childAMm = childIndex === '0' ? desiredChildMm : parentTotal - desiredChildMm
        const rawRatio = ratioForChildSize(childAMm, parentTotal)
        return { ...prev, layout: setSplitRatioAtPath(prev.layout, parentPath, rawRatio) }
      })
    },
    [selectedPath, history, dimensionsLocked]
  )

  const onSave = useCallback(async () => {
    if (!adapter) return
    setSaveStatus('saving')
    setSaveError(undefined)

    // Prune orphan variable metadata before persisting so storage stays in sync
    // with the layout-derived variable set.
    const sanitized: BlockDefinition = {
      ...block,
      variables: pruneOrphans(block.layout, block.variables ?? {}),
    }

    // Brand-new block route (isNewRoute): first save creates a draft. After
    // save, navigate to /blocks/{block.id} so the URL becomes stable.
    if (isDraft || isNewRoute) {
      const res = await adapter.resources.saveBlockDraft(
        sanitized.id,
        sanitized,
        etag && isDraft ? { ifMatch: etag } : undefined
      )
      if (res.status === 'ok') {
        setEtag(res.data.etag)
        setIsDraft(true)
        setSaveStatus('saved')
        setConflictPending(false)
        // Successful save: anchor the clean baseline to the closure's `block`
        // reference — exactly what the dirty check compares history.state
        // against. NOT `sanitized` (a new reference dirty would never match)
        // and NOT history.reset (would wipe the undo stack and clobber edits
        // made while the save was in flight). The in-memory doc keeping inert
        // orphan-variable metadata is fine — it's re-pruned on the next save.
        lastSavedRef.current = block
        if (isNewRoute) {
          navigate(`/blocks/${encodeURIComponent(sanitized.id)}`, { replace: true })
        }
        setTimeout(() => setSaveStatus('idle'), 2000)
      } else if (res.error.code === 'CONFLICT') {
        setSaveStatus('error')
        setSaveError(undefined)
        setConflictPending(true)
      } else {
        setSaveStatus('error')
        setSaveError(res.error.message)
      }
      return
    }

    // Defensive fallback: existing block, not in draft mode — shouldn't reach
    // here because the Save button is hidden in view mode by the JSX below.
    // If we do, this is a bug.
    setSaveStatus('error')
    setSaveError(t('saveError.unexpectedState'))
  }, [adapter, block, etag, isNewRoute, navigate, isDraft, t])

  const onConflictReload = useCallback(async () => {
    // Reload discards the user's local edits (history.reset wipes the doc +
    // undo stack) — guard it like every other destructive action.
    const ok = await confirm({
      title: t('confirm.discardChanges.title'),
      body: t('confirm.discardChanges.body'),
      confirmLabel: t('common.reload'),
      danger: true,
    })
    if (!ok) return // keep the banner + local edits
    setConflictPending(false)
    setSaveStatus('idle')
    startLoad()
  }, [confirm, startLoad, t])

  const onConflictOverwrite = useCallback(async () => {
    if (!adapter || !blockId) return
    setSaveStatus('saving')
    setSaveError(undefined)
    // Silently refetch the draft to get the CURRENT etag, then re-save with it.
    const probe = await adapter.resources.loadBlock(blockId, 'draft')
    if (probe.status !== 'ok') {
      // Can't verify the latest version — NEVER fall through to an
      // unconditional (no-ifMatch) save. Abort; the banner stays armed.
      setSaveStatus('error')
      setSaveError(t('saveError.verifyFailed'))
      return
    }
    const sanitized: BlockDefinition = {
      ...block,
      variables: pruneOrphans(block.layout, block.variables ?? {}),
    }
    const res = await adapter.resources.saveBlockDraft(
      sanitized.id,
      sanitized,
      { ifMatch: probe.data.etag }
    )
    if (res.status === 'ok') {
      setConflictPending(false)
      setEtag(res.data.etag)
      setIsDraft(true)
      setSaveStatus('saved')
      lastSavedRef.current = block
      setTimeout(() => setSaveStatus('idle'), 2000)
    } else if (res.error.code === 'CONFLICT') {
      // Etag moved again between probe and re-save — keep the banner armed,
      // never leak the raw adapter message.
      setSaveStatus('error')
      setConflictPending(true)
    } else {
      setSaveStatus('error')
      setSaveError(res.error.message)
      setConflictPending(false)
    }
  }, [adapter, blockId, block, t])

  const onEditClick = useCallback(async () => {
    if (!adapter || !blockId) return
    setSaveError(undefined)

    // Probe for an existing draft. If one exists, ask for confirmation before
    // replacing it — this is the risky path (e.g. viewing a historical version
    // while a draft of the same block already exists).
    const draftProbe = await adapter.resources.loadBlock(blockId, 'draft')
    if (draftProbe.status === 'ok') {
      // A draft already exists — confirm before clobbering it.
      const ok = await confirm({
        title: t('confirm.replaceDraft.title'),
        body: t('confirm.replaceDraft.body'),
        confirmLabel: t('confirm.replaceDraft.confirmLabel'),
        danger: true,
      })
      if (!ok) return
    } else if (draftProbe.error.code !== 'NOT_FOUND') {
      // Unexpected probe error — surface it and abort.
      setSaveError(draftProbe.error.message)
      return
    }

    const res = await adapter.resources.saveBlockDraft(blockId, block)
    if (res.status === 'ok') {
      setEtag(res.data.etag)
      setIsDraft(true)
    } else {
      setSaveError(res.error.message)
    }
  }, [adapter, blockId, block, confirm, t])

  const onForkClick = useCallback(() => {
    // Kebab "Fork from this version": fork the currently-viewed body (which IS
    // that version — no load needed). Clear any stale forkSource so the modal
    // derives the subtitle from block.version (the currently displayed version).
    setForkSource(undefined)
    setForkOpen(true)
  }, [])

  const onViewVersion = useCallback(async (version: number) => {
    if (!adapter || !blockId) return
    const res = await adapter.resources.loadBlock(blockId, version)
    if (res.status === 'ok') {
      history.reset(healBareLeaves(res.data.resource))
      setEtag(res.data.etag)
      setIsDraft(false)
      setHistoryOpen(false)
      // Re-fetch the version list — otherwise latestPublishedVersion keeps
      // whatever it was at the last runLoad/history-open, and can silently
      // drift out of sync with reality (e.g. a concurrent publish) for the
      // rest of the session.
      const versionsRes = await adapter.resources.listBlockVersions(blockId)
      if (versionsRes.status === 'ok') {
        setVersions(versionsRes.data)
        const max = versionsRes.data.reduce(
          (acc, v) => (v.version > acc ? v.version : acc),
          0
        )
        setLatestPublishedVersion(max > 0 ? max : undefined)
        setVersionsLoaded(true)
      }
    } else {
      setSaveError(res.error.message)
    }
  }, [adapter, blockId, history])

  const onOpenDraftFromHistory = useCallback(async () => {
    if (!adapter || !blockId) return
    const res = await adapter.resources.loadBlock(blockId, 'draft')
    if (res.status === 'ok') {
      history.reset(healBareLeaves(res.data.resource))
      setEtag(res.data.etag)
      setIsDraft(true)
      setHistoryOpen(false)
    } else {
      setSaveError(res.error.message)
    }
  }, [adapter, blockId, history])

  const onForkFromHistory = useCallback(async (version: number) => {
    if (!adapter || !blockId) return
    const res = await adapter.resources.loadBlock(blockId, version)
    if (res.status !== 'ok') {
      setSaveError(res.error.message)
      return
    }
    setForkSource({ body: res.data.resource, version })
    setForkOpen(true)
  }, [adapter, blockId])

  const onForkConfirm = useCallback(
    async (next: { id: string; name: string }) => {
      if (!adapter) return
      // Use the version-loaded body when forking from history; fall back to
      // the current in-memory block when using the kebab "Fork from this version".
      const sourceBody = forkSource?.body ?? block
      const newBody: BlockDefinition = { ...sourceBody, id: next.id, name: next.name }
      const res = await adapter.resources.saveBlockDraft(next.id, newBody)
      if (res.status === 'ok') {
        setForkOpen(false)
        setForkSource(undefined)
        navigate(`/blocks/${encodeURIComponent(next.id)}`)
      } else {
        setSaveError(res.error.message)
      }
    },
    [adapter, block, forkSource, navigate]
  )

  const onDiscardDraft = useCallback(async () => {
    if (!adapter) {
      console.warn('[BlockConfiguratorRoute] Discard draft ignored: adapter not ready')
      return
    }
    const ok = await confirm({
      title: t('confirm.discardDraft.title'),
      body: t('confirm.discardDraft.body'),
      confirmLabel: t('common.discard'),
      danger: true,
    })
    if (!ok) return
    if (!blockId || isNewRoute) {
      // New, never-saved block: nothing persisted to delete — just leave.
      navigate('/blocks', { replace: true })
      return
    }
    const del = await adapter.resources.deleteBlockDraft(blockId)
    if (del.status !== 'ok') {
      // Toast, not setSaveError: the discard failure has to be visible from
      // wherever the user ends up, and we stay put so they can retry (#191).
      toast.error(t('blockConfigurator.discard.error', { message: del.error.message }))
      return
    }
    // Re-load latest as view-only.
    const latest = await adapter.resources.loadBlock(blockId)
    if (latest.status === 'ok') {
      history.reset(healBareLeaves(latest.data.resource))
      setEtag(latest.data.etag)
      setIsDraft(false)
      toast.success(t('blockConfigurator.discard.success'))
      // Re-fetch the version list — the draft that just got discarded may
      // have been masking a stale latestPublishedVersion (e.g. this block
      // was published again by someone else while the draft existed).
      const versionsRes = await adapter.resources.listBlockVersions(blockId)
      if (versionsRes.status === 'ok') {
        setVersions(versionsRes.data)
        const max = versionsRes.data.reduce(
          (acc, v) => (v.version > acc ? v.version : acc),
          0
        )
        setLatestPublishedVersion(max > 0 ? max : undefined)
        setVersionsLoaded(true)
      }
    } else if (latest.error.code === 'NOT_FOUND') {
      // Draft-only block: no published version exists, so there is nothing
      // left to show here — go back to the gallery. The old code set a
      // saveError banner first, but that banner lives inside the
      // loadState==='ok' tree, which the very next navigate() unmounts — the
      // user got zero feedback (#191). A toast survives the navigation
      // because ToastProvider sits above the router.
      //
      // Only NOT_FOUND proves "no published version" — same discrimination the
      // gallery's Duplicate does. Any other code says nothing about what is
      // published (see the branch below).
      toast.success(t('blockConfigurator.discard.successNoPublished'))
      navigate('/blocks', { replace: true })
    } else {
      // The draft IS gone (deleteBlockDraft returned ok), but the reload failed
      // for a reason unrelated to what exists on the server — a bridge timeout
      // surfaces as UPSTREAM_UNAVAILABLE, an auth/server hiccup as
      // SERVER_ERROR. Treating those as "no published version" would toast a
      // green lie and bounce the user to a gallery that still lists the block.
      // Report the partial outcome (error kind: the reload half needs the
      // user's attention) and stay put. `isDraft` deliberately stays true so a
      // Save can re-create the draft from the body still in memory rather than
      // stranding the user's work.
      //
      // The etag must NOT stay, though: it addresses the draft revision this
      // very call deleted. `onSave` sends `{ ifMatch: etag }` whenever
      // `etag && isDraft`, so leaving it armed makes the recovery Save quote a
      // revision that no longer exists — the host rejects it as a CONFLICT and
      // the user lands on a reload-or-overwrite banner over a draft that is
      // simply gone, which is the opposite of the intent above. Dropping it
      // sends the re-create unconditionally, exactly like the new-block route
      // (#191). Nothing is at risk of being clobbered: the draft we would be
      // guarding against is the one we just discarded.
      setEtag(undefined)
      toast.error(
        t('blockConfigurator.discard.successReloadFailed', { message: latest.error.message })
      )
    }
  }, [adapter, blockId, isNewRoute, history, navigate, confirm, toast, t])

  const onPublish = useCallback(async () => {
    if (!adapter || !blockId || isNewRoute) return
    // Non-blocking width heads-up (#132 item f): compares against the
    // Preview pane's own persisted printer-profile selection (same
    // localStorage key, via getStoredPreviewProfile) rather than lifting
    // profileId state out of PreviewPane. Advisory only — publish proceeds
    // regardless; the operator may not have picked a printer yet, or may
    // intend to print on a wider head than whatever the preview happens to
    // be set to right now.
    const previewProfile = getStoredPreviewProfile()
    const usableMm = previewProfile?.usablePrintWidthMm
    const tooWide = usableMm !== undefined && block.widthMm > usableMm
    // Composed in code, not in JSON: the two paragraphs are separate keys so
    // the width warning stays reusable and the blank line stays out of the
    // translation files.
    const baseBody = t('confirm.publishVersion.body')
    const body = tooWide
      ? `${baseBody}\n\n${t('blockConfigurator.publish.widthWarning', {
          widthMm: block.widthMm,
          usableMm,
          profile: previewProfile?.name ?? previewProfile?.id ?? '',
        })}`
      : baseBody
    const confirmed = await confirm({
      title: t('confirm.publishVersion.title', { version: nextVersion }),
      body,
      confirmLabel: t('common.publish'),
    })
    if (!confirmed) return

    // If dirty, save the draft first; abort on failure.
    if (dirty) {
      setSaveStatus('saving')
      setSaveError(undefined)
      const sanitized: BlockDefinition = {
        ...block,
        variables: pruneOrphans(block.layout, block.variables ?? {}),
      }
      const saveRes = await adapter.resources.saveBlockDraft(
        sanitized.id,
        sanitized,
        etag ? { ifMatch: etag } : undefined
      )
      if (saveRes.status !== 'ok') {
        setSaveStatus('error')
        setSaveError(saveRes.error.message)
        return
      }
      setEtag(saveRes.data.etag)
      lastSavedRef.current = block
      setSaveStatus('idle')
    }

    const promoteRes = await adapter.resources.promoteBlockDraft(blockId, block)
    if (promoteRes.status !== 'ok') {
      setSaveError(promoteRes.error.message)
      return
    }

    toast.success(
      t('toast.publishedBlock', { name: block.name, version: promoteRes.data.version })
    )
    // Reload: promote deletes the draft server-side, so a reload lands on
    // the new latest version in view mode.
    startLoad()
  }, [adapter, blockId, isNewRoute, confirm, nextVersion, dirty, block, etag, toast, startLoad, t])

  const onWheelZoom = useCallback((e: React.WheelEvent<HTMLDivElement>) => {
    if (!(e.ctrlKey || e.metaKey)) return
    e.preventDefault()
    setZoom(prev => clampZoom(prev - e.deltaY * 0.001))
  }, [])

  const updateDimensionsFromPointer = useCallback(
    (e: React.PointerEvent<HTMLDivElement>) => {
      if (dimensionsLocked) return
      if (!canvasContainerRef.current || !dragging) return
      const rect = canvasContainerRef.current.getBoundingClientRect()
      const snapMm = snapToGrid ? 5 : 1
      const widthFromPointer = clampBlockDimensionMm(snapToMm((e.clientX - rect.left) / effectivePxPerMm, snapMm))
      const heightFromPointer = clampBlockDimensionMm(snapToMm((e.clientY - rect.top) / effectivePxPerMm, snapMm))
      // Write to transient state — NOT history — so every pointermove doesn't
      // flood the 50-entry undo stack. One history entry is committed on pointerup.
      const base = history.state
      if (dragging === 'right') {
        setTransientBlock({ ...base, widthMm: widthFromPointer })
      } else if (dragging === 'bottom') {
        setTransientBlock({ ...base, heightMm: heightFromPointer })
      } else if (dragging === 'corner') {
        setTransientBlock({ ...base, widthMm: widthFromPointer, heightMm: heightFromPointer })
      }
    },
    [dragging, effectivePxPerMm, snapToGrid, history.state, dimensionsLocked]
  )

  const commitDragDimensions = useCallback(() => {
    if (transientBlock !== undefined) {
      history.set(transientBlock)
      setTransientBlock(undefined)
    }
    setDragging(undefined)
  }, [transientBlock, history])

  const asTemplate = useMemo(
    () => ({
      id: block.id,
      name: block.name,
      widthMm: block.widthMm,
      heightMm: block.heightMm,
      rootZone: block.layout,
    }),
    [block]
  )

  // Synthesised single-instance preview template: the block baked at its own
  // dimensions with variable defaults applied. Memoised on `block`
  // (= transientBlock ?? history.state), so during a drag the preview updates
  // live per pointermove — same as TemplateDesignerRoute; the expensive
  // render is debounced 2s in useRender.
  const previewTemplate = useMemo(() => blockPreviewTemplate(block), [block])

  const defaultCanvasProfile = useMemo(
    () => getPrinterProfile('zebra-zd220-203') ?? getPrinterProfiles()[0],
    []
  )

  const resolvedImages = useResolvedImages(asTemplate, defaultCanvasProfile)

  const { onPaneClick } = useCanvasDeselect({
    onDeselect: () => setSelectedPath(undefined),
    escapeEnabled: !forkOpen,
  })

  // Unsaved-changes guard: beforeunload + LeftNav confirm while dirty.
  // Only active when the load succeeded — non-ok panes can't be saved.
  useUnsavedGuard(loadState === 'ok' && dirty)

  if (loadState === 'loading') {
    return (
      <div data-testid="route-loading" className="flex h-full items-center justify-center text-sm text-stone-500 dark:text-stone-400">
        {t('routeState.loading')}
      </div>
    )
  }

  if (loadState === 'not-found') {
    return (
      <div data-testid="route-not-found" className="flex h-full flex-col items-center justify-center gap-4 text-center">
        <p className="text-base font-medium text-stone-700 dark:text-stone-300">
          {t('routeState.blockNotFound')}
        </p>
        <Link
          to="/blocks"
          className="rounded bg-sky-600 px-4 py-2 text-sm text-white hover:bg-sky-700"
        >
          {t('routeState.backToGallery')}
        </Link>
      </div>
    )
  }

  if (loadState === 'error') {
    return (
      <div data-testid="route-error" className="flex h-full flex-col items-center justify-center gap-4 text-center">
        <p className="text-base font-medium text-stone-700 dark:text-stone-300">
          {t('routeState.blockLoadFailed')}
        </p>
        {/* The adapter/server message stays raw — we translate the frame, not
            somebody else's error text. */}
        {loadError && (
          <p className="text-sm text-stone-500 dark:text-stone-400">{loadError}</p>
        )}
        <button
          type="button"
          data-testid="route-error-retry"
          onClick={startLoad}
          className="rounded bg-sky-600 px-4 py-2 text-sm text-white hover:bg-sky-700"
        >
          {t('routeState.retry')}
        </button>
      </div>
    )
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex items-center justify-between border-b border-stone-200 bg-white px-6 py-3 dark:border-stone-800 dark:bg-stone-900">
        <div className="flex flex-col">
          <h2 className="text-lg font-semibold">{t('blockConfigurator.title')}</h2>
          <div className="mt-1 flex items-center gap-2 text-sm">
            <StatusBadge state={badgeState} />
            <span className="font-medium">{block.name}</span>
          </div>
          <div className="mt-0.5 text-[11px] text-stone-500 dark:text-stone-400">
            {block.widthMm} mm × {block.heightMm} mm ·{' '}
            <span className="font-mono">{block.id}</span>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <CanvasControls
            zoom={zoom}
            onZoomChange={setZoom}
            onZoomToFit={() => {
              const pane = canvasPaneRef.current
              if (!pane) { setZoom(1); return }
              setZoom(fitZoomToViewport(
                { widthMm: block.widthMm, heightMm: block.heightMm },
                { widthPx: pane.clientWidth, heightPx: pane.clientHeight },
                PX_PER_MM_BASE,
              ))
            }}
            snapToGrid={snapToGrid}
            onToggleSnap={() => setSnapToGrid(v => !v)}
          />
          <button
            type="button"
            data-testid="bc-history-toggle"
            onClick={() => setHistoryOpen(o => !o)}
            className="rounded border border-stone-300 px-3 py-1 text-xs text-stone-700 hover:bg-stone-100 dark:border-stone-700 dark:text-stone-300 dark:hover:bg-stone-800"
          >
            {t('blockConfigurator.toolbar.history')}
          </button>
          {isDraft ? (
            <>
              <button
                type="button"
                onClick={history.undo}
                disabled={!history.canUndo}
                className="rounded border border-stone-300 px-3 py-1 text-xs text-stone-700 disabled:opacity-40 hover:enabled:bg-stone-100 dark:border-stone-700 dark:text-stone-300 dark:hover:enabled:bg-stone-800"
              >
                {t('blockConfigurator.toolbar.undo')}
              </button>
              <button
                type="button"
                onClick={history.redo}
                disabled={!history.canRedo}
                className="rounded border border-stone-300 px-3 py-1 text-xs text-stone-700 disabled:opacity-40 hover:enabled:bg-stone-100 dark:border-stone-700 dark:text-stone-300 dark:hover:enabled:bg-stone-800"
              >
                {t('blockConfigurator.toolbar.redo')}
              </button>
              <button
                type="button"
                data-testid="bc-discard-draft"
                onClick={onDiscardDraft}
                disabled={!adapter}
                className="rounded border border-rose-300 px-3 py-1 text-xs text-rose-700 disabled:opacity-40 hover:enabled:bg-rose-50 dark:border-rose-700 dark:text-rose-300 dark:hover:enabled:bg-rose-950"
              >
                {t('blockConfigurator.toolbar.discardDraft')}
              </button>
              <button
                type="button"
                data-testid="bc-save"
                onClick={onSave}
                disabled={!adapter || saveStatus === 'saving' || newBlockSaveBlocker !== undefined}
                title={newBlockSaveBlocker}
                className="rounded bg-sky-600 px-3 py-1 text-xs text-white disabled:opacity-40 hover:enabled:bg-sky-700"
              >
                {saveStatus === 'saving'
                  ? t('blockConfigurator.toolbar.saving')
                  : saveStatus === 'saved'
                    ? t('blockConfigurator.toolbar.saved')
                    : dirty
                      ? t('blockConfigurator.toolbar.saveDirty')
                      : t('blockConfigurator.toolbar.save')}
              </button>
              <button
                type="button"
                data-testid="bc-publish"
                onClick={onPublish}
                disabled={!adapter || saveStatus === 'saving' || isNewRoute}
                title={isNewRoute ? t('blockConfigurator.toolbar.publishTooltip') : undefined}
                className="rounded bg-emerald-600 px-3 py-1 text-xs text-white disabled:opacity-40 hover:enabled:bg-emerald-700"
              >
                {t('blockConfigurator.toolbar.publish', { version: nextVersion })}
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                data-testid="bc-toolbar-edit"
                onClick={onEditClick}
                className="rounded bg-sky-600 px-3 py-1 text-xs text-white hover:bg-sky-700"
              >
                {t('blockConfigurator.toolbar.edit')}
              </button>
              <ViewKebabMenu onFork={onForkClick} />
            </>
          )}
        </div>
      </div>
      {conflictPending && (
        <div
          data-testid="conflict-banner"
          className="flex items-center gap-3 bg-amber-50 px-6 py-2 text-xs text-amber-800 dark:bg-amber-950 dark:text-amber-200"
        >
          <span>{t('blockConfigurator.conflict.message')}</span>
          <button
            type="button"
            data-testid="conflict-reload"
            onClick={onConflictReload}
            className="rounded border border-amber-400 px-2 py-0.5 hover:bg-amber-100 dark:border-amber-600 dark:hover:bg-amber-900"
          >
            {t('blockConfigurator.conflict.reload')}
          </button>
          <button
            type="button"
            data-testid="conflict-overwrite"
            onClick={onConflictOverwrite}
            className="rounded border border-rose-400 px-2 py-0.5 text-rose-700 hover:bg-rose-50 dark:border-rose-600 dark:text-rose-300 dark:hover:bg-rose-950"
          >
            {t('blockConfigurator.conflict.overwrite')}
          </button>
        </div>
      )}
      {saveError && (
        <div className="bg-rose-50 px-6 py-2 text-xs text-rose-700 dark:bg-rose-950 dark:text-rose-300">
          {saveError}
        </div>
      )}
      <SplitPane initialTopPct={65} storageKey="ld:block-configurator-split">
        <div className="flex h-full overflow-hidden">
          <aside className="flex w-64 shrink-0 flex-col overflow-auto border-r border-stone-200 bg-white dark:border-stone-800 dark:bg-stone-900">
            <BlockMetadataEditor
              block={block}
              onChange={onChangeMetadata}
              disabled={!isDraft}
              existsInStorage={!isNewRoute && loadState === 'ok'}
              existingIds={isNewRoute ? existingBlockIds : []}
              onIdValidChange={isNewRoute ? setIdValid : undefined}
            />
          </aside>
          <div
            className="flex flex-1 flex-col overflow-auto bg-stone-100 dark:bg-stone-950"
            onWheel={onWheelZoom}
          >
            <RegionTable
              block={block}
              selectedZoneId={selectedZoneId}
              onSelect={zone => setSelectedPath(zone.path)}
            />
            <div
              ref={canvasPaneRef}
              data-testid="bc-canvas-pane"
              className="flex flex-1 items-start p-10"
              onClick={onPaneClick}
            >
              {/* 2×2 grid: corner cell (inputs + lock), horizontal ruler,
                  vertical ruler, canvas. Each ruler shares its row/column
                  with the canvas, so tick 0 and the final tick align exactly
                  with the label edges — the old flex layout offset the
                  rulers by the corner inputs, overshooting the label extent.
                  Centered via mx-auto on THIS grid, not justify-center on the
                  scroll pane above: justify-center on an overflow-auto
                  container distributes overflow symmetrically, so once the
                  grid is wider than the viewport its start (left) edge
                  becomes unreachable by scrolling. margin-auto degrades
                  gracefully instead — it collapses to 0 and the grid simply
                  starts flush left, reachable, once it no longer fits. */}
              <div
                className="mx-auto inline-grid"
                style={{ gridTemplateColumns: 'auto auto', gridTemplateRows: 'auto auto' }}
              >
                <div className="flex flex-col items-end gap-1 pb-1 pr-1">
                  <RulerDimensionInput
                    valueMm={block.widthMm}
                    orientation="horizontal"
                    disabled={!isDraft || dimensionsLocked}
                    onChange={onChangeWidth}
                  />
                  <div className="flex items-center gap-1">
                    <DimensionLockButton
                      locked={dimensionsLocked}
                      disabled={!isDraft}
                      onToggle={onToggleDimensionsLock}
                    />
                    <RulerDimensionInput
                      valueMm={block.heightMm}
                      orientation="vertical"
                      disabled={!isDraft || dimensionsLocked}
                      onChange={onChangeHeight}
                    />
                  </div>
                </div>
                <div className="self-end">
                  <Ruler
                    orientation="horizontal"
                    lengthMm={block.widthMm}
                    pxPerMm={PX_PER_MM_BASE}
                    zoom={zoom}
                  />
                </div>
                <div className="justify-self-end">
                  <Ruler
                    orientation="vertical"
                    lengthMm={block.heightMm}
                    pxPerMm={PX_PER_MM_BASE}
                    zoom={zoom}
                  />
                </div>
                <div ref={canvasContainerRef} data-testid="bc-canvas" className="relative">
                  <Canvas
                    template={asTemplate}
                    pxPerMm={effectivePxPerMm}
                    selectedZoneId={isDraft ? selectedZoneId : undefined}
                    data={undefined}
                    variables={variablesForPanel}
                    mode="authoring"
                    resolvedImages={resolvedImages}
                    onSelect={isDraft ? onSelect : undefined}
                    onSplit={isDraft ? onSplit : undefined}
                    onSplitRatioChange={isDraft ? onSplitRatioChange : undefined}
                    onSplitRatioDragEnd={isDraft ? onSplitRatioDragEnd : undefined}
                    onMergeAtPath={isDraft ? onMergeAtPath : undefined}
                  />
                  {isDraft && !dimensionsLocked && (
                      <>
                        <div
                          data-testid="bounds-handle-right"
                          className="absolute -right-1 top-0 z-10 h-full w-2 cursor-ew-resize bg-sky-400/20 hover:bg-sky-400/40"
                          onPointerDown={e => {
                            e.currentTarget.setPointerCapture(e.pointerId)
                            setDragging('right')
                          }}
                          onPointerMove={updateDimensionsFromPointer}
                          onPointerUp={e => {
                            if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                              e.currentTarget.releasePointerCapture(e.pointerId)
                            }
                            commitDragDimensions()
                          }}
                        />
                        <div
                          data-testid="bounds-handle-bottom"
                          className="absolute -bottom-1 left-0 z-10 h-2 w-full cursor-ns-resize bg-sky-400/20 hover:bg-sky-400/40"
                          onPointerDown={e => {
                            e.currentTarget.setPointerCapture(e.pointerId)
                            setDragging('bottom')
                          }}
                          onPointerMove={updateDimensionsFromPointer}
                          onPointerUp={e => {
                            if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                              e.currentTarget.releasePointerCapture(e.pointerId)
                            }
                            commitDragDimensions()
                          }}
                        />
                        <div
                          data-testid="bounds-handle-corner"
                          className="absolute -right-1 -bottom-1 z-10 h-3 w-3 cursor-nwse-resize bg-sky-500/40 hover:bg-sky-500/60"
                          onPointerDown={e => {
                            e.currentTarget.setPointerCapture(e.pointerId)
                            setDragging('corner')
                          }}
                          onPointerMove={updateDimensionsFromPointer}
                          onPointerUp={e => {
                            if (e.currentTarget.hasPointerCapture(e.pointerId)) {
                              e.currentTarget.releasePointerCapture(e.pointerId)
                            }
                            commitDragDimensions()
                          }}
                        />
                      </>
                    )}
                  </div>
              </div>
            </div>
          </div>
          {isDraft ? (
            <RegionPropertyPanel
              selectedZoneId={selectedZoneId}
              selected={selectedPrimitive}
              selectedZoneStyle={selectedZoneStyle}
              selectedBorders={selectedBorders}
              selectedZoneWidthMm={selectedZoneForPanel?.w}
              selectedZoneHeightMm={selectedZoneForPanel?.h}
              variables={variablesForPanel}
              onChangePrimitive={onChangeComponent}
              onChangeZoneStyle={onChangeZoneStyle}
              onChangeVariableMeta={onChangeVariableMeta}
              onChangePlacement={onChangePlacement}
              onSetEdge={onSetEdge}
              onZoneDimensionsChange={onZoneDimensionsChange}
              // Root-zone region-size edits resize the block itself (mirrors
              // the ruler inputs) — read-only while locked. A split child's
              // edit only adjusts the parent ratio, so it stays live even
              // when locked (matches onZoneDimensionsChange's own gate).
              regionSizeDisabled={dimensionsLocked && selectedPath?.length === 0}
            />
          ) : (
            <aside className="flex w-72 shrink-0 flex-col border-l border-stone-200 bg-white p-4 dark:border-stone-800 dark:bg-stone-900">
              <h3 className="text-sm font-semibold">{t('blockConfigurator.viewMode.title')}</h3>
              <p className="mt-2 text-xs text-stone-500 dark:text-stone-400">
                {t('blockConfigurator.viewMode.body')}
              </p>
            </aside>
          )}
          {historyOpen && (
            <VersionHistoryPanel
              blockId={block.id}
              versions={versions}
              hasDraft={isDraft}
              onView={onViewVersion}
              onOpenDraft={onOpenDraftFromHistory}
              onFork={onForkFromHistory}
              onClose={() => setHistoryOpen(false)}
            />
          )}
        </div>
        <PreviewPane template={previewTemplate} />
      </SplitPane>
      {forkOpen && (
        <ForkBlockModal
          sourceId={block.id}
          sourceName={block.name}
          sourceVersion={forkSource?.version ?? block.version}
          existingIds={existingBlockIds}
          listLoading={blockListLoading}
          listFailed={blockListFailed}
          onCancel={() => { setForkOpen(false); setForkSource(undefined) }}
          onConfirm={onForkConfirm}
        />
      )}
    </div>
  )
}
